import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

const C = {
  ink:    '#0A0A0A',
  blue:   '#4F8EF7',
  muted:  '#888888',
  muted2: '#C0C0C0',
  border: '#EBEBEB',
  bg:     '#F7F7F7',
  surface:'#FFFFFF',
  red:    '#D32F2F',
  display:"'Barlow Condensed', sans-serif",
  mono:   "'Space Grotesk', sans-serif",
}

const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { font-family: 'Space Grotesk', sans-serif; background: ${C.bg}; color: ${C.ink}; }
  ::placeholder { color: ${C.muted2}; font-family: 'Space Grotesk', sans-serif; }
  @keyframes spin { to { transform: rotate(360deg); } }
  input:focus, select:focus { outline: none; }
  select option { background: #fff; color: #0A0A0A; }
`

const RECAPTCHA_SITE_KEY = '6LejkZ4sAAAAHqk-vJfSbj3_N4xpc5Z94KakHga'

export default function Signup() {
  const navigate = useNavigate()
  const { signup } = useAuth()

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    role: '', clubName: '', city: '', password: '', confirmPassword: '',
  })
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(false)
  const [focused, setFocused] = useState(null)
  const [recaptchaReady, setRecaptchaReady] = useState(false)

  useEffect(() => {
    const scriptId = 'recaptcha-v3'
    if (document.getElementById(scriptId)) { setRecaptchaReady(true); return }
    const script = document.createElement('script')
    script.id = scriptId
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    script.onload = () => setRecaptchaReady(true)
    document.head.appendChild(script)
  }, [])

  const cleanPhone = (v) => v.replace(/[^0-9+\s\-().]/g, '')

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('')
    if (formData.password !== formData.confirmPassword) { setError('Les mots de passe ne correspondent pas'); return }
    if (formData.password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères'); return }
    if (!formData.firstName.trim() || !formData.lastName.trim()) { setError('Prénom et nom requis'); return }
    const phoneDigits = formData.phone.replace(/\D/g, '')
    if (phoneDigits.length < 10) { setError('Numéro de téléphone invalide (10 chiffres minimum)'); return }
    if (!formData.clubName.trim()) { setError('Nom du club requis'); return }
    if (!formData.role) { setError('Poste requis'); return }
    if (!formData.city.trim()) { setError('Ville requise'); return }
    setLoading(true)
    try {
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`
      let recaptchaToken = ''
      try {
        await new Promise(resolve => window.grecaptcha.ready(resolve))
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'signup' })
      } catch (e) {
        console.warn('reCAPTCHA indisponible', e)
      }
      await signup({
        name: fullName, email: formData.email, password: formData.password,
        plan: 'COACH', phone: formData.phone.trim(),
        club_name: formData.clubName.trim(), city: formData.city.trim(),
        role: formData.role, recaptcha_token: recaptchaToken,
      })
      navigate('/onboarding')
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Une erreur est survenue.'
      setError(msg); setLoading(false)
    }
  }

  const inputStyle = (name) => ({
    width: '100%', height: 44, padding: '0 12px',
    fontFamily: C.mono, fontSize: 13, color: C.ink,
    background: C.surface,
    border: `0.5px solid ${focused === name ? C.ink : C.border}`,
    borderRadius: 3, transition: 'border-color .12s',
    appearance: 'none',
  })

  const labelStyle = {
    fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '1px',
    textTransform: 'uppercase', color: C.muted2, display: 'block', marginBottom: 6,
  }

  const fields = [
    { label: 'Prénom',       name: 'firstName',       type: 'text',     ph: '' },
    { label: 'Nom',          name: 'lastName',        type: 'text',     ph: '' },
    { label: 'Email',        name: 'email',           type: 'email',    ph: 'votre@email.com' },
    { label: 'Téléphone',    name: 'phone',           type: 'tel',      ph: '06 12 34 56 78' },
    { label: 'Poste',        name: 'role',            type: 'select',   options: ['Éducateur', 'Entraîneur', 'Directeur Sportif', 'Analyste Vidéo'] },
    { label: 'Nom du club',  name: 'clubName',        type: 'text',     ph: '' },
    { label: 'Ville',        name: 'city',            type: 'text',     ph: '' },
    { label: 'Mot de passe', name: 'password',        type: 'password', ph: '••••••••', hint: '8 caractères minimum' },
    { label: 'Confirmer',    name: 'confirmPassword', type: 'password', ph: '••••••••' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <style>{CSS}</style>

      {/* Header */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 64,
        background: C.surface, borderBottom: `1.5px solid ${C.ink}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', zIndex: 100,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img src="/logo.svg" alt="Insightball" style={{ width: 28, height: 28 }} />
          <span style={{ fontFamily: C.display, fontSize: 18, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: C.ink }}>
            INSIGHT<span style={{ color: C.blue }}>BALL</span>
          </span>
        </Link>
        <Link to="/login" style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink, textDecoration: 'none', transition: 'opacity .12s' }}
          onMouseEnter={e => e.target.style.opacity = '0.55'}
          onMouseLeave={e => e.target.style.opacity = '1'}>
          Déjà un compte
        </Link>
      </header>

      {/* Contenu */}
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 48px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <p style={{ fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: C.blue, marginBottom: 10 }}>
              Créer un compte
            </p>
            <h1 style={{ fontFamily: C.display, fontSize: 44, fontWeight: 900, textTransform: 'uppercase', lineHeight: 0.9, color: C.ink }}>
              Démarrez<br />aujourd'hui.
            </h1>
          </div>

          <div style={{
            background: C.surface, border: `0.5px solid ${C.border}`,
            borderTop: `2.5px solid ${C.blue}`, borderRadius: 3, padding: '36px 32px',
          }}>

            {error && (
              <div style={{ marginBottom: 20, padding: '12px 14px', background: '#FFF0F0', borderLeft: `2px solid ${C.red}`, borderRadius: 2 }}>
                <p style={{ fontFamily: C.mono, fontSize: 11, color: C.red, margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {fields.map(f => (
                <div key={f.name} style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>{f.label}</label>
                  {f.type === 'select' ? (
                    <select value={formData[f.name]}
                      onChange={e => setFormData({ ...formData, [f.name]: e.target.value })}
                      required
                      style={{ ...inputStyle(f.name), backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      onFocus={() => setFocused(f.name)} onBlur={() => setFocused(null)}>
                      <option value="" disabled>Sélectionner</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={formData[f.name]}
                      onChange={e => {
                        const val = f.name === 'phone' ? cleanPhone(e.target.value) : e.target.value
                        setFormData({ ...formData, [f.name]: val })
                      }}
                      placeholder={f.ph} required style={inputStyle(f.name)}
                      onFocus={() => setFocused(f.name)} onBlur={() => setFocused(null)} />
                  )}
                  {f.hint && <p style={{ fontFamily: C.mono, fontSize: 9, color: C.muted2, marginTop: 4 }}>{f.hint}</p>}
                </div>
              ))}

              <button type="submit" disabled={loading} style={{
                width: '100%', marginTop: 8, height: 44,
                background: loading ? '#555' : C.ink, color: '#FFFFFF',
                fontFamily: C.mono, fontSize: 12, fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase',
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                {loading
                  ? <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .6s linear infinite' }} /> Création...</>
                  : <>Créer mon compte <ArrowRight size={13} /></>
                }
              </button>
            </form>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `0.5px solid ${C.border}`, textAlign: 'center' }}>
              <p style={{ fontFamily: C.mono, fontSize: 12, color: C.muted }}>
                Déjà un compte ?{' '}
                <Link to="/login" style={{ color: C.blue, textDecoration: 'none', fontWeight: 600 }}>Se connecter</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
